import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { Text as RNText, TouchableOpacity, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { baseColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AuroraAICoachAvatarIcon } from '../../atoms/icons/AuroraAICoachAvatarIcon'
import { Avatar, avatarAltTextGeneric, AvatarDetails } from '../../atoms/icons/Avatar'
import { NumberBadge } from '../../atoms/icons/NumberBadge'
import { TextBadge } from '../../atoms/icons/TextBadge'
import { Title } from '../../atoms/title/Title'
import { BadgeVariant } from '../../badge/constants'
import { ListItem } from '../../molecules/listItem/ListItem'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { getFriendlyFormattedTimeStamp } from '../../utils/getFriendlyFormattedTimeStamp'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

/**
 * This component represents a single MessageThread in a threadlist
 * Each MessageThread is a conversation with a provider
 */

export type MessageThreadProps = {
  avatarDetails: AvatarDetails
  threadTitleMsg: string
  threadSubTitleMsg: string
  threadMsg: string
  conversationId: string
  textBadgeMsg?: string
  timeStampMsg: string
  unreadMsgCount: number
  onThreadPressed: (conversationId: string) => void
  isSidebarView?: boolean
  isSelected?: boolean
  isAICoach?: boolean
  childBadgeLabel?: string
}

const AvatarContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  justifyContent: 'center',
  marginVertical: theme.spacing['24px'],
  marginLeft: theme.spacing['16px'],
  marginRight: theme.spacing['12px'],
}))

const ThreadContent = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  // Center a title-only row (e.g. the single "Lyra AI" sidebar entry) on the avatar and selected indicator; rows
  // with a subtitle are taller than the avatar, so this has no effect on them.
  justifyContent: 'center',
  marginVertical: theme.spacing['24px'],
  marginRight: theme.spacing['16px'],
  flex: 1,
  flexShrink: 1,
  minWidth: 0,
}))

const TitleRow = styled.View({
  alignItems: 'center',
  flexDirection: 'row',
  width: '100%',
})

const ThreadTitle = styled(Title)({
  flexShrink: 1,
  minWidth: 0,
})

const MessageSubtitleContainer = styled.View({
  marginTop: '2px',
  flexDirection: 'row',
})

const ThreadMessageRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  marginTop: theme.spacing['4px'],
}))

const ThreadMessage = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  flex: 1,
  flexShrink: 1,
  height: theme.spacing['40px'],
  minWidth: 0,
}))

const TextBadgeContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing['12px'],
}))

const TimeStamp = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  flexShrink: 0,
  marginLeft: 'auto',
  paddingLeft: theme.spacing['8px'],
}))

const NumberBadgeContainer = styled.View<{ theme: ThemeType; isSidebarView: boolean }>(({ theme, isSidebarView }) => ({
  ...(isSidebarView
    ? {
        justifyContent: 'center',
        marginRight: theme.spacing['16px'],
      }
    : {
        alignSelf: 'flex-start',
        flexShrink: 0,
        marginLeft: theme.spacing['8px'],
        marginRight: theme.spacing['8px'],
        marginTop: '2px',
      }),
}))

const SelectedIndicator = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  height: '56px',
  width: '3px',
  backgroundColor: theme.colors.borderActive,
  borderTopRightRadius: '2px',
  borderBottomRightRadius: '2px',
}))

const ChildBadgeContainer = styled.View<{ theme: ThemeType; isSidebarView: boolean }>(({ theme, isSidebarView }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(theme.isAuroraTheme && !isSidebarView, theme.colors.backgroundPrimary),
  paddingLeft: isSidebarView ? theme.spacing['16px'] : theme.spacing['24px'],
  paddingTop: theme.spacing['8px'],
}))

const ChildBadgePill = styled.View<{ theme: ThemeType }>(() => ({
  alignSelf: 'flex-start',
  backgroundColor: baseColors.peri_50,
  paddingHorizontal: 10,
  paddingVertical: 4,
  borderRadius: 999,
  height: 24,
  justifyContent: 'center',
}))

const ChildBadgeText = styled(RNText)<{ theme: ThemeType }>(() => ({
  fontSize: 12,
  lineHeight: 16,
  fontWeight: '500',
  textAlign: 'center',
  color: baseColors.peri_20,
}))

export const MessageThread: FunctionComponent<MessageThreadProps> = ({
  avatarDetails,
  threadTitleMsg,
  threadSubTitleMsg,
  threadMsg,
  onThreadPressed,
  textBadgeMsg,
  timeStampMsg,
  conversationId,
  unreadMsgCount,
  isSidebarView = false,
  isSelected,
  isAICoach = false,
  childBadgeLabel,
  children,
}) => {
  const showTimeStamp = Boolean(timeStampMsg)
  const intl = useIntl()
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const avatarSize = 32

  return (
    <View testID={tID('MessageThread')}>
      {childBadgeLabel && (
        <ChildBadgeContainer isSidebarView={isSidebarView}>
          <ChildBadgePill
            testID={tID('MessageThread-childBadge')}
            accessibilityRole='text'
            accessibilityLabel={childBadgeLabel}
          >
            <ChildBadgeText>{childBadgeLabel}</ChildBadgeText>
          </ChildBadgePill>
        </ChildBadgeContainer>
      )}
      <TouchableOpacity onPress={() => onThreadPressed(conversationId)}>
        <ListItem
          dividerWidth={isSidebarView ? '100%' : '90%'}
          dividerColor={isAuroraTheme ? colors.borderDefault : undefined}
          backgroundColor={getAuroraAwareBackgroundColor(isAuroraTheme && !isSidebarView, colors.backgroundPrimary)}
          {...(isSelected && {})}
        >
          {isSidebarView && isSelected && <SelectedIndicator style={{ transform: [{ translateY: '-50%' }] }} />}
          <AvatarContainer>
            {isAICoach && isAuroraTheme ? (
              <AuroraAICoachAvatarIcon size={avatarSize} />
            ) : (
              <Avatar
                details={avatarDetails}
                accessibilityLabel={intl.formatMessage(avatarAltTextGeneric, { name: avatarDetails.displayName })}
                size={avatarSize}
              />
            )}
          </AvatarContainer>
          <ThreadContent>
            <TitleRow testID={tID('MessageThread-titleRow')}>
              <ThreadTitle
                text={threadTitleMsg}
                size={TitleSize.XSMALL}
                noRole
                numberOfLines={isSidebarView && isAICoach ? 2 : 1}
                // Figma's title-xsmall style uses 1.2 leading; the shared token is intentionally looser.
                style={{ lineHeight: 19.2 }}
                testID={tID('MessageThread-title')}
              />
              {!isSidebarView && Boolean(textBadgeMsg) && (
                <TextBadgeContainer>
                  <TextBadge
                    text={textBadgeMsg ?? ''}
                    variant={BadgeVariant.ALERT}
                    testID={tID('MessageThread-liveSessionBadge')}
                  />
                </TextBadgeContainer>
              )}
              {!isSidebarView && showTimeStamp && (
                <TimeStamp
                  /* getFriendlyFormattedTimeStamp handles internationalization */
                  text={getFriendlyFormattedTimeStamp(intl, timeStampMsg)}
                  size={BodyTextSize.CAPTION}
                  color={colors.textSecondary}
                  testID={tID('MessageThread-timestamp')}
                />
              )}
            </TitleRow>
            {Boolean(threadSubTitleMsg) && (
              <MessageSubtitleContainer>
                <BodyText text={threadSubTitleMsg} size={BodyTextSize.SMALL} color={colors.textSecondary} />
              </MessageSubtitleContainer>
            )}
            {!isSidebarView && (
              <ThreadMessageRow>
                <ThreadMessage text={threadMsg} numberOfLines={2} size={BodyTextSize.SMALL} />
                {unreadMsgCount > 0 && (
                  <NumberBadgeContainer testID={tID('MessageThread-unreadMsgCount')} isSidebarView={false}>
                    <NumberBadge number={String(unreadMsgCount)} />
                  </NumberBadgeContainer>
                )}
              </ThreadMessageRow>
            )}
          </ThreadContent>
          {isSidebarView && unreadMsgCount > 0 && (
            <NumberBadgeContainer testID={tID('MessageThread-unreadMsgCount')} isSidebarView>
              <NumberBadge number={String(unreadMsgCount)} />
            </NumberBadgeContainer>
          )}
          {children}
        </ListItem>
      </TouchableOpacity>
    </View>
  )
}

export default MessageThread
